<template>
  <div class="system-settings-container">
    <!-- 页面头部 -->
    <div class="settings-header">
      <div class="header-content">
        <i class="el-icon-setting header-icon"></i>
        <div class="header-text">
          <h2 class="page-title">系统设置</h2>
          <p class="page-subtitle">管理系统各项参数和配置</p>
        </div>
      </div>
    </div>

    <el-card v-loading="loading.load" class="settings-card">
      <!-- 移动端标签选择器 -->
      <div class="mobile-tab-selector" v-if="isMobile">
        <el-select
            v-model="activeTab"
            placeholder="请选择设置类型"
            class="mobile-tab-select"
            size="large"
        >
          <el-option label="预约设置" value="appointment"></el-option>
          <el-option label="通知设置" value="notification"></el-option>
        </el-select>
      </div>

      <!-- 桌面端标签页 -->
      <el-tabs v-model="activeTab" v-else class="desktop-tabs">
        <el-tab-pane label="预约设置" name="appointment"></el-tab-pane>
        <el-tab-pane label="通知设置" name="notification"></el-tab-pane>
      </el-tabs>

      <div class="tab-content">
        <!-- 预约设置 -->
        <div v-if="activeTab === 'appointment'" class="settings-section">
          <div class="section-header">
            <i class="el-icon-date section-icon"></i>
            <h3 class="section-title">预约设置</h3>
            <p class="section-description">配置学员预约相关参数</p>
          </div>

          <el-form
              :model="appointmentSettings"
              :label-position="isMobile ? 'top' : 'left'"
              class="settings-form"
          >
            <div class="settings-grid">
              <div
                  v-for="(item, index) in appointmentItems"
                  :key="'app-'+index"
                  class="setting-item-card"
              >
                <div class="setting-content-wrapper">
                  <div class="setting-header">
                    <span class="setting-label">{{ item.label }}</span>
                    <el-tooltip
                        :content="getTooltipContent(item)"
                        placement="top"
                        effect="light"
                    >
                      <i class="el-icon-question help-icon"></i>
                    </el-tooltip>
                  </div>
                  <div class="setting-control">
                    <el-input-number
                        v-model="appointmentSettings[item.model]"
                        :min="item.min"
                        :max="item.max"
                        :step="item.step || 1"
                        controls-position="right"
                        size="large"
                        class="standard-input-number"
                    ></el-input-number>
                    <span class="unit-text">{{ item.unit }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="form-actions">
              <el-button
                  type="primary"
                  @click="saveAppointmentSettings"
                  :loading="loading.appointment"
                  class="save-btn primary-btn"
                  size="large"
              >
                <i class="el-icon-check"></i>
                保存预约设置
              </el-button>
            </div>
          </el-form>
        </div>

        <!-- 通知设置 -->
        <div v-if="activeTab === 'notification'" class="settings-section">
          <div class="section-header">
            <i class="el-icon-bell section-icon"></i>
            <h3 class="section-title">通知设置</h3>
            <p class="section-description">配置系统通知相关参数</p>
          </div>

          <el-form
              :model="notificationSettings"
              :label-position="isMobile ? 'top' : 'left'"
              class="settings-form"
          >
            <div class="settings-grid">
              <div
                  v-for="(item, index) in notificationItems"
                  :key="'not-'+index"
                  class="setting-item-card"
              >
                <div class="setting-content-wrapper">
                  <div class="setting-header">
                    <span class="setting-label">{{ item.label }}</span>
                    <el-tooltip
                        :content="getTooltipContent(item)"
                        placement="top"
                        effect="light"
                    >
                      <i class="el-icon-question help-icon"></i>
                    </el-tooltip>
                  </div>
                  <div class="setting-control">
                    <template v-if="item.type === 'number'">
                      <el-input-number
                          v-model="notificationSettings[item.model]"
                          :min="item.min"
                          :max="item.max"
                          controls-position="right"
                          size="large"
                          class="standard-input-number"
                      ></el-input-number>
                      <span class="unit-text">{{ item.unit }}</span>
                    </template>
                    <div class="switch-control" v-else>
                      <el-switch
                          v-model="notificationSettings[item.model]"
                          active-color="#13ce66"
                          inactive-color="#ff4949"
                          class="enhanced-switch"
                      ></el-switch>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="form-actions">
              <el-button
                  type="primary"
                  @click="saveNotificationSettings"
                  :loading="loading.notification"
                  class="save-btn primary-btn"
                  size="large"
              >
                <i class="el-icon-check"></i>
                保存通知设置
              </el-button>
            </div>
          </el-form>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
// 导入真实的API
import { systemAPI } from '@/services/api'

export default {
  name: 'SystemSettings',
  data() {
    return {
      activeTab: 'appointment',
      messagePosition: 'top-right',
      isMobile: false,
      appointmentSettings: {
        advanceDays: null,
        minAdvanceHours: null,
        maxDuration: null,
        maxDailyAppointments: null,
        freeCancelHours: null
      },
      notificationSettings: {
        reminderHours: null,
        smsEnabled: null,
        wechatEnabled: null,
        notifyCoach: null
      },
      loading: {
        appointment: false,
        notification: false,
        load: false
      }
    }
  },
  computed: {
    // 预约设置项配置
    appointmentItems() {
      return [
        {
          label: '可提前预约天数',
          model: 'advanceDays',
          min: 1,
          max: 30,
          unit: '天',
          description: '学员可以提前多少天进行预约'
        },
        {
          label: '最少提前预约小时',
          model: 'minAdvanceHours',
          min: 1,
          max: 24,
          unit: '小时',
          description: '预约时需要至少提前多少小时'
        },
        {
          label: '单次预约最大时长',
          model: 'maxDuration',
          min: 2,
          max: 8,
          unit: '小时',
          description: '单次预约最多可以预约多长时间'
        },
        {
          label: '学员每日最大预约次数',
          model: 'maxDailyAppointments',
          min: 1,
          max: 5,
          unit: '次',
          description: '学员每天最多可以预约多少次'
        },
        {
          label: '免费取消提前时间',
          model: 'freeCancelHours',
          min: 1,
          max: 24,
          unit: '小时',
          description: '提前多少小时取消预约不收取费用'
        }
      ]
    },
    // 通知设置项配置
    notificationItems() {
      return [
        {
          label: '预约提醒提前时间',
          model: 'reminderHours',
          type: 'number',
          min: 1,
          max: 24,
          unit: '小时',
          description: '提前多少小时发送预约提醒'
        },
        {label: '启用短信通知', model: 'smsEnabled', type: 'switch', description: '是否启用短信通知功能'},
        {label: '启用微信通知', model: 'wechatEnabled', type: 'switch', description: '是否启用微信通知功能'},
        {label: '新预约通知教练', model: 'notifyCoach', type: 'switch', description: '有新预约时是否通知教练'}
      ]
    }
  },
  methods: {
    getTooltipContent(item) {
      return item.description || item.label
    },
    // 检查是否为移动端
    checkIsMobile() {
      const width = window.innerWidth
      this.isMobile = width <= 768
      if (width <= 480) {
        this.messagePosition = 'top'
      } else if (width <= 768) {
        this.messagePosition = 'top'
      } else {
        this.messagePosition = 'top-right'
      }
    },
    // 显示消息
    showMessage(options) {
      const config = {
        duration: 3000,
        showClose: true,
        ...options
      }
      if (this.isMobile) {
        config.position = 'top'
        config.duration = 2000
      } else {
        config.position = this.messagePosition
      }
      this.$message(config)
    },
    // 加载设置
    async loadSettings() {
      this.loading.load = true
      try {
        console.log('正在加载系统设置...')
        const response = await systemAPI.getSettings()
        if (response.data) {
          this.appointmentSettings = {
            advanceDays: response.data.advanceDays,
            minAdvanceHours: response.data.minAdvanceHours,
            maxDuration: response.data.maxDuration,
            maxDailyAppointments: response.data.maxDailyAppointments,
            freeCancelHours: response.data.freeCancelHours
          }
          this.notificationSettings = {
            reminderHours: response.data.reminderHours,
            smsEnabled: response.data.smsEnabled,
            wechatEnabled: response.data.wechatEnabled,
            notifyCoach: response.data.notifyCoach
          }
        }
        console.log('系统设置加载完成')
        this.showMessage({message: '系统设置加载成功', type: 'success'})
      } catch (error) {
        console.error('加载系统设置失败:', error)
        this.showMessage({
          message: '加载系统设置失败: ' + (error.response?.data?.message || error.message),
          type: 'error'
        })
      } finally {
        this.loading.load = false
      }
    },
    // 保存预约设置
    async saveAppointmentSettings() {
      this.loading.appointment = true
      try {
        await systemAPI.saveAppointmentSettings(this.appointmentSettings)
        this.showMessage({message: '预约设置保存成功', type: 'success'})
      } catch (error) {
        console.error('保存预约设置失败:', error)
        this.showMessage({message: '保存失败: ' + (error.response?.data?.message || error.message), type: 'error'})
      } finally {
        this.loading.appointment = false
      }
    },
    // 保存通知设置
    async saveNotificationSettings() {
      this.loading.notification = true
      try {
        await systemAPI.saveNotificationSettings(this.notificationSettings)
        this.showMessage({message: '通知设置保存成功', type: 'success'})
      } catch (error) {
        console.error('保存通知设置失败:', error)
        this.showMessage({message: '保存失败: ' + (error.response?.data?.message || error.message), type: 'error'})
      } finally {
        this.loading.notification = false
      }
    }
  },
  async mounted() {
    this.checkIsMobile()
    window.addEventListener('resize', this.checkIsMobile)
    await this.loadSettings()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkIsMobile)
  }
}
</script>

<style scoped>
.system-settings-container {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e7ed 100%);
  min-height: 100vh;
}

/* 页面头部 */
.settings-header {
  margin-bottom: 24px;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.header-icon {
  font-size: 32px;
  color: #409EFF;
  padding: 12px;
  background: linear-gradient(135deg, #ecf5ff, #d9ecff);
  border-radius: 50%;
}

.header-text {
  flex: 1;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: #303133;
  margin-bottom: 8px;
  background: linear-gradient(135deg, #409EFF, #67C23A);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.page-subtitle {
  font-size: 16px;
  color: #606266;
  margin: 0;
}

/* 主卡片 */
.settings-card {
  border-radius: 16px;
  border: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

/* 移动端标签选择器 */
.mobile-tab-selector {
  padding: 20px 20px 0;
}

.mobile-tab-select {
  width: 100%;
}

.mobile-tab-select ::v-deep .el-input__inner {
  height: 52px;
  border-radius: 12px;
  font-size: 16px;
  border: 2px solid #e4e7ed;
}

/* 桌面端标签页 */
.desktop-tabs {
  padding: 0 20px;
}

.desktop-tabs ::v-deep .el-tabs__header {
  margin-bottom: 0;
}

.desktop-tabs ::v-deep .el-tabs__nav-wrap::after {
  height: 1px;
}

.desktop-tabs ::v-deep .el-tabs__item {
  font-size: 16px;
  font-weight: 500;
  padding: 0 24px;
  height: 60px;
}

.desktop-tabs ::v-deep .el-tabs__item.is-active {
  color: #409EFF;
  font-weight: 600;
}

.desktop-tabs ::v-deep .el-tabs__active-bar {
  background-color: #409EFF;
  height: 3px;
  border-radius: 2px;
}

/* 标签内容 */
.tab-content {
  padding: 0;
}

.settings-section {
  padding: 0 20px 20px;
}

/* 区域头部 */
.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 24px 0 20px;
  border-bottom: 1px solid #ebeef5;
  margin-bottom: 24px;
}

.section-icon {
  font-size: 24px;
  color: #409EFF;
  padding: 8px;
  background: #ecf5ff;
  border-radius: 8px;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
  margin: 0;
}

.section-description {
  font-size: 14px;
  color: #909399;
  margin: 0 0 0 auto;
}

/* 设置表单 */
.settings-form {
  margin-top: 0;
}

.settings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}

/* 设置项卡片布局 */
.setting-item-card {
  background: #f8f9fa;
  border: 1px solid #ebeef5;
  border-radius: 12px;
  padding: 24px;
  transition: all 0.3s ease;
  min-height: 120px;
  display: flex;
  align-items: center;
}

.setting-content-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.setting-item-card:hover {
  border-color: #409EFF;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.1);
  transform: translateY(-2px);
}

.setting-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0;
}

.setting-label {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  flex: 1;
  margin-right: 8px;
}

.help-icon {
  color: #909399;
  cursor: help;
  font-size: 16px;
  transition: color 0.3s ease;
  flex-shrink: 0;
}

.help-icon:hover {
  color: #409EFF;
}

.setting-control {
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: space-between;
}

/* 标准 Element UI 输入框样式 - 使用默认垂直按钮 */
.standard-input-number {
  flex: 1;
  max-width: 200px;
}

.standard-input-number ::v-deep .el-input-number {
  width: 100%;
}

.standard-input-number ::v-deep .el-input-number__decrease,
.standard-input-number ::v-deep .el-input-number__increase {
  background: #f5f7fa;
  border-color: #dcdfe6;
  color: #606266;
}

.standard-input-number ::v-deep .el-input-number__decrease:hover,
.standard-input-number ::v-deep .el-input-number__increase:hover {
  color: #409EFF;
}

.standard-input-number ::v-deep .el-input-number__decrease.is-disabled,
.standard-input-number ::v-deep .el-input-number__increase.is-disabled {
  color: #c0c4cc;
  background-color: #f5f7fa;
  cursor: not-allowed;
}

.standard-input-number ::v-deep .el-input__inner {
  height: 40px;
  border-radius: 8px;
  border: 2px solid #e4e7ed;
  font-size: 16px;
  text-align: center;
}

.standard-input-number ::v-deep .el-input__inner:focus {
  border-color: #409EFF;
}

.unit-text {
  font-size: 14px;
  color: #909399;
  font-weight: 500;
  min-width: 40px;
  text-align: center;
}

/* 开关控件容器 */
.switch-control {
  display: flex;
  justify-content: flex-end;
  flex: 1;
  max-width: 200px;
}

.enhanced-switch {
  transform: scale(1.2);
}

/* 操作按钮 */
.form-actions {
  display: flex;
  justify-content: center;
  padding-top: 24px;
  border-top: 1px solid #ebeef5;
}

.save-btn {
  height: 48px;
  padding: 0 40px;
  font-size: 16px;
  font-weight: 500;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.primary-btn {
  background: linear-gradient(135deg, #409EFF, #337ecc);
  border: none;
}

.primary-btn:hover {
  background: linear-gradient(135deg, #66b1ff, #409EFF);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
}

/* 响应式设计 - 移动端 */
@media (max-width: 768px) {
  .system-settings-container {
    padding: 12px;
  }

  .header-content {
    padding: 16px;
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }

  .page-title {
    font-size: 24px;
  }

  .settings-section {
    padding: 0 16px 16px;
  }

  .section-header {
    flex-direction: column;
    text-align: center;
    gap: 8px;
    padding: 16px 0 20px;
  }

  .section-description {
    margin: 0;
  }

  .settings-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .setting-item-card {
    padding: 20px;
    min-height: auto;
  }

  .setting-content-wrapper {
    gap: 12px;
  }

  .setting-control {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .standard-input-number {
    max-width: 150px;
  }

  .switch-control {
    max-width: none;
    justify-content: flex-end;
  }

  .enhanced-switch {
    transform: scale(1.1);
  }

  .save-btn {
    width: 100%;
    height: 52px;
    font-size: 18px;
  }
}

/* 超小屏幕优化 */
@media (max-width: 480px) {
  .settings-grid {
    grid-template-columns: 1fr;
  }

  .setting-item-card {
    padding: 16px;
  }

  .section-header {
    padding: 12px 0 16px;
  }

  .setting-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .setting-control {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .standard-input-number {
    max-width: 100%;
  }

  .switch-control {
    justify-content: center;
    max-width: 100%;
  }

  .unit-text {
    text-align: center;
    order: -1;
    margin-bottom: 8px;
  }
}

/* 加载状态优化 */
::v-deep .el-loading-mask {
  border-radius: 16px;
}

::v-deep .el-card__header {
  padding: 0;
}

::v-deep .el-card__body {
  padding: 0;
}

/* 确保所有父容器不会裁剪内容 */
.system-settings-container,
.settings-card,
.tab-content,
.settings-section,
.settings-form,
.settings-grid {
  overflow: visible !important;
}

/* 针对开关按钮的特别处理 */
::v-deep .el-switch {
  max-width: 100%;
}
</style>
